import React, {
  FC,
  memo,
  useState,
  useRef,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'umi';
import classNames from 'classnames';
import BasicImage from '@home/components/BasicImage';
import HighlightText from '@/pages/aigc/components/HighlightText';
import CategoryTabs from '@/pages/aigc/pages/Agent/SkillsCollection/components/CategoryTabs';
import SkillsSelectSearchRow from '@/pages/aigc/pages/Agent/SkillsCollection/components/SkillsSelectSearchRow';
import { useSkillsData } from '@/pages/aigc/pages/Agent/SkillsCollection/hooks';
import { buildSelectedSkillTag } from '@/pages/aigc/pages/Agent/SkillsCollection/utils/skillTag';
import {
  ISkillData,
  ALL_CATEGORY,
} from '@/pages/aigc/pages/Agent/SkillsCollection/types';
import type { ISelectedSkillTagData } from './SelectedSkillTag';
import SkillsDefaultIcon from '@/pages/aigc/assets/svg/skills-default-icon.svg';
import { navigateToSkillDetail } from '@/pages/aigc/pages/Agent/SkillsCollection/utils/navigation';
import SkillSlashIcon from '@/pages/aigc/assets/svg/skill-slash-icon.svg';
import SkillSlashActiveIcon from '@/pages/aigc/assets/svg/icon-skill.svg';
import { useHover, useControllableValue } from 'ahooks';
import type { TSkillMentionOpenReason } from './hooks/useSkillMentionHost';
import { useSkillMentionPreview } from './hooks/useSkillMentionPreview';
import { useDismissOutside } from '../hooks/useDismissOutside';
import { useMentionDropdownPosition } from '../mentionShared/useMentionDropdownPosition';
import MentionDropdownSkeleton from '../mentionShared/MentionDropdownSkeleton';
import type { ICleanupTriggerBehavior } from '../mentionShared/useMentionTrigger';
import { buildSkillInlineLabel } from '../utils/inlineMentionLabel';
import './index.scss';

interface ISkillMentionProps {

  isEN?: boolean;
  selectedSkills?: ISelectedSkillTagData[];
  onSelectedChange?: (skills: ISelectedSkillTagData[]) => void;
  className?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** @ 后面的查询关键词（由 useSkillMentionHost 提供） */
  mentionQuery?: string;
  /** 打开的来源：'button'（点击按钮）| 'slash'（输入 / 触发） */
  openReason?: TSkillMentionOpenReason;
  /** 虚拟锚点元素 ref（@ 触发时定位到 @ 字符下方） */
  mentionAnchorRef?: React.RefObject<HTMLElement | null>;
  /** QAInput 容器 ref，用于计算下拉框位置和宽度（与输入框两侧对齐） */
  inputBoxContainerRef?: React.RefObject<HTMLElement | null>;
  /** 是否向下展开下拉框（agentHome 场景或编辑模式向下，其他场景向上） */
  isDropDown?: boolean;
  /** 外部控制的键盘高亮索引（@ 模式下由 useSkillMentionKeyboard 管理） */
  externalHighlightIndex?: number;
  /** 过滤后的 skill 列表变更回调，用于同步给外部 keyboard hook */
  onFilteredSkillsChange?: (skills: ISkillData[]) => void;
  /** 选中 skill 后插入引用串（/ 模式替换触发符；button 模式在光标处追加） */
  onSlashModeSelect?: (
    insertLabel?: string,
    behavior?: ICleanupTriggerBehavior,
  ) => void;
  /** 是否允许 hover 打开下拉，默认 true；Copilot 传 false 仅 click 打开 */
  enableHoverOpen?: boolean;
  /** 来源，Copilot 下按钮只展示图标 */
  from?: string;
}

const PREFIX = 'dba-skill-mention';

const SkillMention: FC<ISkillMentionProps> = ({
  isEN = false,
  selectedSkills = [],
  onSelectedChange,
  className,
  open,
  onOpenChange,
  mentionQuery = '',
  openReason = 'button',
  mentionAnchorRef,
  inputBoxContainerRef,
  isDropDown = false,
  externalHighlightIndex,
  onFilteredSkillsChange,
  onSlashModeSelect,
  enableHoverOpen = true,
  from,
}) => {
  const isCopilot = from === 'copilot';
  const [dropdownVisible, rawSetDropdownVisible] =
    useControllableValue<boolean>(
      { value: open, onChange: onOpenChange },
      { defaultValue: false, valuePropName: 'value', trigger: 'onChange' },
    );
  const [activeCategory, setActiveCategory] = useState(ALL_CATEGORY);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const clearPreviewRef = useRef<() => void>(() => {});

  const setDropdownVisible = useCallback(
    (visible: boolean | ((prev: boolean) => boolean)) => {
      const newVisible =
        typeof visible === 'function' ? visible(dropdownVisible) : visible;
      rawSetDropdownVisible(newVisible);
      if (!newVisible) {
        setSearchKeyword('');
        setActiveCategory(ALL_CATEGORY);
        setHighlightIndex(-1);
        // 关闭时重置点击标记
        isSelectClickedRef.current = false;
        isButtonTriggeredOpenRef.current = false;
        clearPreviewRef.current();
      }
      // 打开时不在此处设置 highlightIndex，交由下方 useEffect 根据最新的 openReason 决定
    },
    [dropdownVisible, rawSetDropdownVisible],
  );

  // 当下拉框打开后，根据最新的 openReason 决定是否默认选中第一项
  // 解决时序问题：button 模式下 openReason 通过 onOpenChange 回调链异步更新，
  // 在 setDropdownVisible 闭包中拿到的还是旧值（可能是上一次 'at'）
  useEffect(() => {
    if (dropdownVisible) {
      setHighlightIndex(openReason === 'slash' ? 0 : -1);
    }
  }, [dropdownVisible, openReason]);

  /** 是否处于 @ 输入触发模式 */
  const isSlashMode = openReason === 'slash' && dropdownVisible;

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // ─── 类似 AgentSelector 的 hover 触发 & 延迟关闭逻辑 ───
  // 标记是否是通过点击列表元素触发的关闭（用于阻止 hover 离开时自动关闭）
  const isSelectClickedRef = useRef(false);
  // 标记本次是否由 Skill 按钮触发打开，规避 openReason 异步更新导致分支抖动
  const isButtonTriggeredOpenRef = useRef(false);
  // 延迟关闭的定时器
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);

  const { formatMessage } = useIntl();

  // 使用 useHover 监听 hover 状态
  const isDropdownHovering = useHover(dropdownRef);
  const isTriggerHovering = useHover(triggerRef);

  // 监听外部 open 变化，当外部强制关闭时，清除延迟关闭定时器
  useEffect(() => {
    if (open !== undefined && !open) {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      isSelectClickedRef.current = false;
      isButtonTriggeredOpenRef.current = false;
    }
  }, [open]);

  // 监听 hover 状态，自动关闭下拉菜单（仅 button 触发模式且未点击列表时有效）
  useEffect(() => {
    if (!enableHoverOpen) return;
    if (!dropdownVisible || openReason !== 'button') return;

    // 如果是通过点击列表元素触发的，不自动关闭（持续保持打开，让用户可以连续选择）
    if (isSelectClickedRef.current) return;

    // 清除之前的定时器
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    // 如果鼠标在触发按钮或下拉菜单内，保持打开状态
    if (isDropdownHovering || isTriggerHovering) return;

    // 延迟关闭，给用户时间从触发按钮移动到下拉菜单
    closeTimerRef.current = setTimeout(() => {
      setDropdownVisible(false);
    }, 200);

    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, [enableHoverOpen, isTriggerHovering, isDropdownHovering, dropdownVisible, openReason]);

  // hover 触发打开：含 / 输入态，悬停按钮时切回 button 布局
  const handleTriggerHover = useCallback(() => {
    if (!enableHoverOpen) return;
    isButtonTriggeredOpenRef.current = true;
    if (openReason !== 'button') {
      onOpenChange?.(true);
    }
    setDropdownVisible(true);
  }, [enableHoverOpen, openReason, onOpenChange, setDropdownVisible]);

  const { dropdownPos } = useMentionDropdownPosition({
    enabled: dropdownVisible,
    isDropDown,
    layout: openReason === 'button' ? 'trigger' : 'container',
    forceTriggerLayoutRef: isButtonTriggeredOpenRef,
    dropdownRef,
    triggerRef,
    inputBoxContainerRef,
    mentionAnchorRef,
  });

  const { installedSkills, categoryTabs: installedCategoryTabs, loading } =
    useSkillsData(isEN);

  /**
   * 有效的过滤关键词：
   * - 按钮触发模式 → 使用面板内搜索框的 searchKeyword
   * - @ 输入触发模式 → 使用 mentionQuery（即 @ 后面输入的内容）
   */
  const filterKeyword = useMemo(() => {
    if (openReason === 'slash' && dropdownVisible) {
      return mentionQuery.trim();
    }
    return searchKeyword.trim();
  }, [openReason, dropdownVisible, mentionQuery, searchKeyword]);

  // 已选中的 skillId 集合，用于快速查找
  const selectedSkillIds = useMemo(
    () => new Set(selectedSkills.map((s) => s.skillId)),
    [selectedSkills],
  );

  const filteredSkills = useMemo(() => {
    // dialogable=0 不支持对话；缺省按 1。按钮下拉与 / 唤起共用此列表
    let result = installedSkills.filter(
      (skill) => Number(skill.dialogable ?? 1) === 1,
    );

    // 1. 过滤掉已选中的 skill，避免重复显示
    if (selectedSkillIds.size > 0) {
      result = result.filter((skill) => !selectedSkillIds.has(skill.skill_id));
    }

    // 2. 按分类过滤
    if (activeCategory !== ALL_CATEGORY) {
      result = result.filter((skill) => skill.category_id === activeCategory);
    }

    // 3. 按关键词搜索过滤
    const trimmed = filterKeyword.trim();
    if (trimmed) {
      const kw = trimmed.toLowerCase();
      result = result.filter((skill) => {
        const blob = (isEN
          ? [skill.display_name_en, skill.shortDescription]
          : [skill.display_name_cn, skill.shortDescription_cn]
        )
          .filter((x) => x && String(x).trim())
          .join(' ')
          .toLowerCase();
        return blob.includes(kw);
      });
    }
    return result;
  }, [installedSkills, selectedSkillIds, activeCategory, filterKeyword, isEN]);

  // 过滤列表变化时通知外部（供 useSkillMentionKeyboard 使用）
  useEffect(() => {
    onFilteredSkillsChange?.(filteredSkills);
  }, [filteredSkills]); // eslint-disable-line react-hooks/exhaustive-deps

  const maxIdx = Math.max(filteredSkills.length - 1, 0);
  // @ 模式下使用外部传入的高亮索引（由 useSkillMentionKeyboard 管理），按钮模式下使用内部状态
  const rawHighlightIndex = isSlashMode && externalHighlightIndex !== undefined
    ? externalHighlightIndex
    : highlightIndex;
  // -1 表示无选中，保持 -1；否则限制在有效范围内
  const keyboardHighlightIndex = rawHighlightIndex < 0
    ? -1
    : Math.min(rawHighlightIndex, maxIdx);

  const isInsideDropdown = useCallback((target: EventTarget | null): boolean => {
    if (!target || !(target instanceof Node)) {
      return false;
    }
    return Boolean(
      listRef.current?.contains(target)
      || dropdownRef.current?.contains(target),
    );
  }, []);

  const {
    previewSkill,
    previewPanelRef,
    clearPreview,
    handleItemMouseEnter,
    handleItemMouseLeave,
    isPreviewActive,
  } = useSkillMentionPreview({
    isEN,
    dropdownVisible,
    listRef,
    dropdownRef,
    keyboardHighlightIndex,
    filteredSkills,
    isInsideDropdown,
    onCloseDropdown: () => setDropdownVisible(false),
    previewPlacement: isCopilot ? 'left' : 'right',
  });
  clearPreviewRef.current = clearPreview;

  // 外部受控关闭（如 / 模式 Enter）不经过 setDropdownVisible，须同步关闭预览浮窗
  useEffect(() => {
    if (!dropdownVisible) {
      clearPreviewRef.current();
    }
  }, [dropdownVisible]);

  // 面板外点击关闭；预览浮窗为独立 Portal，须纳入白名单（避免滚动/点详情误关）
  useDismissOutside({
    enabled: dropdownVisible || Boolean(previewSkill),
    onClose: () => {
      setDropdownVisible(false);
      clearPreview();
    },
    insideSelectors: isSlashMode && inputBoxContainerRef
      ? [dropdownRef, containerRef, inputBoxContainerRef, previewPanelRef]
      : [dropdownRef, containerRef, previewPanelRef],
  });

  useEffect(() => {
    if (!enableHoverOpen) return;
    if (!dropdownVisible || openReason !== 'button') return;
    if (isSelectClickedRef.current) return;

    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }

    if (isDropdownHovering || isTriggerHovering || isPreviewActive) {
      return;
    }

    closeTimerRef.current = setTimeout(() => {
      setDropdownVisible(false);
    }, 200);

    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
      }
    };
  }, [
    enableHoverOpen,
    isTriggerHovering,
    isDropdownHovering,
    isPreviewActive,
    dropdownVisible,
    openReason,
    setDropdownVisible,
  ]);

  /**
   * 按需注册全局 scroll(capture) + mousemove：仅在 dropdownVisible 时订阅
   */
  useEffect(() => {
    if (!dropdownVisible) return;
    const onScroll = (e: Event) => {
      const target = e.target as HTMLElement;
      if (dropdownRef.current?.contains(target)) return;
      if (previewPanelRef.current?.contains(target)) return;
      setDropdownVisible(false);
    };
    const onMove = (e: MouseEvent) => {
      if (e.buttons <= 0) return;
      const target = e.target as Node;
      // 点击 Skill 按钮打开时，mousedown 后 mousemove 仍落在 trigger 上，须排除否则会立刻关闭
      if (containerRef.current?.contains(target)) return;
      if (dropdownRef.current?.contains(target)) return;
      if (previewPanelRef.current?.contains(target)) return;
      setDropdownVisible(false);
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('mousemove', onMove);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('mousemove', onMove);
    };
  }, [dropdownVisible, previewPanelRef, setDropdownVisible]);

  // ─── 自动滚动高亮项到可见区域 ───
  useEffect(() => {
    if (!dropdownVisible || filteredSkills.length === 0 || keyboardHighlightIndex < 0) return;
    const list = listRef.current;
    if (!list) return;
    const items = list.querySelectorAll(`.${PREFIX}__item`);
    const target = items[keyboardHighlightIndex] as HTMLElement | undefined;
    if (target) {
      target.scrollIntoView({ block: 'nearest' });
    }
  }, [keyboardHighlightIndex, dropdownVisible, filteredSkills.length]);

  const handleTriggerClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    isButtonTriggeredOpenRef.current = true;
    if (!dropdownVisible) {
      setSearchKeyword('');
      setActiveCategory(ALL_CATEGORY);
    }
    onOpenChange?.(true);
    setDropdownVisible(true);
  }, [dropdownVisible, onOpenChange, setDropdownVisible]);


  const handleSelectSkill = useCallback(
    (skill: ISkillData) => {
      // 已选中的 skill 不做任何操作（不支持反选取消，只能通过标签关闭按钮移除）
      if (selectedSkills.some((s) => s.skillId === skill.skill_id)) {
        return;
      }
      const tagData: ISelectedSkillTagData = buildSelectedSkillTag(skill, isEN);
      onSelectedChange?.([...selectedSkills, tagData]);
      const insertLabel = buildSkillInlineLabel(tagData, isEN);
      const buttonModeBehavior: ICleanupTriggerBehavior = {
        closeDropdown: false,
        insertAtSelection: true,
      };
      onSlashModeSelect?.(
        insertLabel,
        isSlashMode ? undefined : buttonModeBehavior,
      );

      if (isSlashMode) {
        setDropdownVisible(false);
      } else {
        isSelectClickedRef.current = true;
      }
    },
    [selectedSkills, onSelectedChange, isSlashMode, onSlashModeSelect, setDropdownVisible, isEN],
  );

  /** 面板内 keyDown（面板自身获焦时使用） */
  const handlePanelKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!dropdownVisible) return;

      const target = e.target as HTMLElement;
      if (target.closest('input') || target.closest('textarea')) {
        return;
      }

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (filteredSkills.length === 0) return;
          setHighlightIndex((prev) => (prev < 0 ? 0 : Math.min(prev + 1, maxIdx)));
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (filteredSkills.length === 0) return;
          setHighlightIndex((prev) => (prev < 0 ? 0 : Math.max(prev - 1, 0)));
          break;
        case 'Enter':
          e.preventDefault();
          if (keyboardHighlightIndex >= 0 && filteredSkills[keyboardHighlightIndex]) {
            handleSelectSkill(filteredSkills[keyboardHighlightIndex]);
          }
          break;
        case 'Escape':
          setDropdownVisible(false);
          break;
        default:
          break;
      }
    },
    [
      dropdownVisible,
      filteredSkills,
      keyboardHighlightIndex,
      maxIdx,
      handleSelectSkill,
      setDropdownVisible,
    ],
  );

  const handleCategoryChange = useCallback((categoryKey: string) => {
    setActiveCategory(categoryKey);
    setHighlightIndex(-1);
    clearPreview();
  }, [clearPreview]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchKeyword(value);
    setHighlightIndex(-1);
    clearPreview();
  }, [clearPreview]);

  const panel = (
    <div
      className={`${PREFIX}__dropdown`}
      onKeyDown={handlePanelKeyDown}
      onMouseDown={(e) => { if (isSlashMode) e.preventDefault(); }}
      onClick={() => {
        isSelectClickedRef.current = true;
      }}
    >
      {/* @ 触发模式下隐藏搜索框，因为查询直接来自输入框；Tab 栏始终展示 */}
      {!isSlashMode && (
        <SkillsSelectSearchRow
          isEN={isEN}
          value={searchKeyword}
          onChange={handleSearchChange}
        />
      )}
      {/* 分类 TAB（超过 2 个才显示） */}
      {installedCategoryTabs.length > 2 && (
        <CategoryTabs
          activeCategory={activeCategory}
          onChange={handleCategoryChange}
          tabs={installedCategoryTabs}
          className="dba-skills-select-panel-tabs"
        />
      )}

      <div ref={listRef} className={`${PREFIX}__list`}>
        {loading && <MentionDropdownSkeleton />}
        {!loading && filteredSkills.length === 0 && (
          <div className={`${PREFIX}__empty`}>
            {isEN ? 'No more Skills available' : '暂无更多Skill'}
          </div>
        )}
        {!loading && filteredSkills.map((skill, index) => {
          const itemLabel = isEN
            ? (skill.display_name_en?.trim() || skill.skill_name || '')
            : (skill.display_name_cn?.trim() || skill.skill_name_cn || skill.skill_name || '');
          const shortDesc = isEN ? skill.shortDescription : skill.shortDescription_cn;
          return (
            <div
              key={skill.skill_id}
              className={classNames(`${PREFIX}__item`, {
                [`${PREFIX}__item--highlight`]:
                  index === keyboardHighlightIndex,
              })}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSelectSkill(skill);
              }}
              onMouseEnter={(e) => {
                handleItemMouseEnter(skill, e.currentTarget);
              }}
              onMouseLeave={handleItemMouseLeave}
            >
              <BasicImage
                src={skill.category_icon}
                alt={itemLabel}
                errorSrc={SkillsDefaultIcon}
                className={`${PREFIX}__item-icon`}
              />
              <div className={`${PREFIX}__item-content`}>
                <span className={`${PREFIX}__item-name`}>
                  <span className={`${PREFIX}__item-name-text`}>
                    <HighlightText
                      name={itemLabel}
                      keyword={filterKeyword}
                    />
                  </span>
                </span>
                {shortDesc && (
                  <span className={`${PREFIX}__item-desc`}>
                    <HighlightText
                      name={shortDesc}
                      keyword={filterKeyword}
                    />
                  </span>
                )}
              </div>
              <span
                className={`${PREFIX}__item-detail-btn`}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  navigateToSkillDetail(skill.skill_id);
                }}
              >
                {formatMessage({ id: 'dba_skills_mention_detail_link' })}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <>
      <div
        ref={containerRef}
        className={classNames(PREFIX, className, {
          [`${PREFIX}--icon-only`]: isCopilot,
        })}
      >
        <button
          ref={triggerRef}
          type="button"
          className={classNames(`${PREFIX}__trigger`, {
            // 输入 / 打开下拉时不应让按钮呈现 hover 高亮
            [`${PREFIX}__trigger--active`]:
              dropdownVisible &&
              (openReason === 'button' || isButtonTriggeredOpenRef.current),
          })}

          onClick={handleTriggerClick}
          onMouseEnter={enableHoverOpen ? handleTriggerHover : undefined}
        >
          <span className={`${PREFIX}__trigger-icon-wrap`}>
            <img
              src={SkillSlashIcon}
              alt=""
              className={classNames(
                `${PREFIX}__trigger-icon`,
                `${PREFIX}__trigger-icon--idle`,
                `${PREFIX}__trigger-icon--slash`,
              )}
            />
            <img
              src={SkillSlashActiveIcon}
              alt=""
              className={classNames(
                `${PREFIX}__trigger-icon`,
                `${PREFIX}__trigger-icon--hover`,
                `${PREFIX}__trigger-icon--slash`,
              )}
            />
          </span>
          {!isCopilot && <span className={`${PREFIX}__trigger-text`}>Skill</span>}
        </button>
      </div>

      {dropdownVisible &&
        dropdownPos &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            className={classNames(
              `${PREFIX}__dropdown-overlay`,
              'dba-agent-selector-dropdown-overlay',
              'dba-nav-menu-overlay',
              {
                [`${PREFIX}__dropdown-overlay--up`]: !isDropDown,
                [`${PREFIX}__dropdown-overlay--down`]: isDropDown,
                [`${PREFIX}__dropdown-overlay--button`]:
                  openReason === 'button' || isButtonTriggeredOpenRef.current,

              },
            )}
            style={{
              position: 'fixed',
              ...(dropdownPos.top !== undefined ? { top: dropdownPos.top } : {}),
              ...(dropdownPos.bottom !== undefined ? { bottom: dropdownPos.bottom } : {}),
              left: dropdownPos.left,
              ...(dropdownPos.width !== undefined ? { width: dropdownPos.width } : {}),
              zIndex: 2001,
              boxSizing: 'border-box',
            }}
          >
            {panel}
          </div>,
          document.body,
        )}

      {/* 右侧预览浮窗已移除，详情按钮直接显示在下拉列表项上 */}
      {/* {previewPortal} */}
    </>
  );
};

export default memo(SkillMention);
